import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Plus, Search, Workflow, MoreVertical, Play, 
  Copy, Trash2, Edit, CheckCircle, Clock, Sparkles,
  ArrowUpDown, Calendar, Activity
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import NLWorkflowGenerator from "@/components/NLWorkflowGenerator";

export default function WorkflowsPage() {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("recent"); // recent, a-z, last-run
  const [showNLGenerator, setShowNLGenerator] = useState(false);

  const fetchWorkflows = async () => {
    try {
      const response = await axios.get(`${API}/workflows`, { withCredentials: true });
      setWorkflows(response.data);
    } catch (error) {
      console.error('Error fetching workflows:', error);
      toast.error("Failed to load workflows");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const handleDuplicate = async (workflowId) => {
    try {
      await axios.post(`${API}/workflows/${workflowId}/duplicate`, {}, { withCredentials: true });
      toast.success("Workflow duplicated");
      fetchWorkflows();
    } catch (error) {
      toast.error("Failed to duplicate workflow");
    }
  };

  const handleDelete = async (workflowId) => {
    if (!window.confirm("Are you sure you want to delete this workflow?")) return;
    try {
      await axios.delete(`${API}/workflows/${workflowId}`, { withCredentials: true });
      toast.success("Workflow deleted");
      fetchWorkflows();
    } catch (error) {
      toast.error("Failed to delete workflow");
    }
  };

  const handleExecute = async (workflowId) => {
    try {
      await axios.post(`${API}/workflows/${workflowId}/execute`, {}, { withCredentials: true });
      toast.success("Workflow executed successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to execute workflow");
    }
  };

  const filteredWorkflows = workflows.filter(w => 
    w.name.toLowerCase().includes(search.toLowerCase()) ||
    w.description?.toLowerCase().includes(search.toLowerCase())
  );

  // Sort workflows based on selected sort option
  const sortedWorkflows = [...filteredWorkflows].sort((a, b) => {
    switch (sortBy) {
      case 'recent':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'a-z':
        return a.name.localeCompare(b.name);
      case 'last-run':
        if (!a.last_run && !b.last_run) return 0;
        if (!a.last_run) return 1;
        if (!b.last_run) return -1;
        return new Date(b.last_run) - new Date(a.last_run);
      default:
        return 0;
    }
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="workflows-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-semibold text-foreground">Workflows</h1>
            <p className="text-muted-foreground mt-1">Create and manage your automation workflows</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => navigate('/workflows/generate')}
              className="rounded-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              data-testid="ai-generate-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" /> AI Generate
            </Button>
            <Button 
              onClick={() => navigate('/workflows/new')}
              className="rounded-full bg-primary hover:bg-primary/90"
              data-testid="create-workflow-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Create Workflow
            </Button>
          </div>
        </div>

        {/* Search and Sort Controls */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder="Search workflows..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-full"
              data-testid="search-workflows"
            />
          </div>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[200px] rounded-full" data-testid="sort-dropdown">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Sort by..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent" data-testid="sort-recent">Most Recent</SelectItem>
              <SelectItem value="a-z" data-testid="sort-a-z">A-Z by Name</SelectItem>
              <SelectItem value="last-run" data-testid="sort-last-run">Last Executed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Workflows Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <Card key={i} className="glass-card animate-pulse">
                <CardContent className="p-6">
                  <div className="h-32 bg-muted/50 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : sortedWorkflows.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedWorkflows.map((workflow) => (
              <Card 
                key={workflow.workflow_id}
                className="glass-card hover:-translate-y-1 transition-transform duration-300 cursor-pointer group"
                onClick={() => navigate(`/workflows/${workflow.workflow_id}`)}
                data-testid={`workflow-card-${workflow.workflow_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Workflow className="w-6 h-6 text-primary" />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/workflows/${workflow.workflow_id}`); }}>
                          <Edit className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        {workflow.status === 'published' && (
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleExecute(workflow.workflow_id); }}>
                            <Play className="w-4 h-4 mr-2" /> Execute
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicate(workflow.workflow_id); }}>
                          <Copy className="w-4 h-4 mr-2" /> Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={(e) => { e.stopPropagation(); handleDelete(workflow.workflow_id); }}
                          className="text-red-500"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-2 line-clamp-1">
                    {workflow.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {workflow.description || "No description"}
                  </p>

                  {/* Status and Schedule Info */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                        workflow.status === 'published' 
                          ? 'bg-green-500/10 text-green-500' 
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {workflow.status === 'published' ? (
                          <CheckCircle className="w-3 h-3" />
                        ) : (
                          <Clock className="w-3 h-3" />
                        )}
                        {workflow.status}
                      </span>
                      
                      {formatSchedule(workflow) && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatSchedule(workflow)}
                        </span>
                      )}
                    </div>

                    {/* Created and Last Run Timestamps */}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>Created {formatDate(workflow.created_at)}</span>
                      </div>
                      {workflow.last_run && (
                        <div className="flex items-center gap-1">
                          <Activity className="w-3 h-3" />
                          <span>Ran {formatDate(workflow.last_run)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Footer with Stats */}
                  <div className="flex items-center justify-between pt-3 border-t">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{workflow.nodes?.length || 0} nodes</span>
                      <span>•</span>
                      <span>{workflow.run_count || 0} runs</span>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={(e) => { e.stopPropagation(); handleExecute(workflow.workflow_id); }}
                      disabled={workflow.status !== 'published'}
                      className="rounded-full"
                    >
                      <Play className="w-3 h-3 mr-1" /> Run
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="glass-card">
            <CardContent className="py-16 text-center">
              <Workflow className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                {search ? "No workflows found" : "No workflows yet"}
              </h3>
              <p className="text-muted-foreground mb-6">
                {search ? "Try a different search term" : "Create your first workflow to get started"}
              </p>
              {!search && (
                <Button 
                  onClick={() => navigate('/workflows/new')}
                  className="rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" /> Create Workflow
                </Button>
              )}


            </CardContent>
          </Card>
        )}
      </div>

      {/* Natural Language Workflow Generator */}
      <NLWorkflowGenerator 
        isOpen={showNLGenerator} 
        onClose={() => setShowNLGenerator(false)}
        onWorkflowCreated={() => {
          setShowNLGenerator(false);
          fetchWorkflows();
        }}
      />
    </DashboardLayout>
  );
}



// Helper function to format dates as relative time (e.g., "5m ago", "2h ago", "3d ago")
function formatDate(dateString) {
  if (!dateString) return 'Never';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);

  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  if (diffWeek < 4) return `${diffWeek}w ago`;
  if (diffMonth < 12) return `${diffMonth}mo ago`;
  return `${diffYear}y ago`;
}

// Helper function to format schedule cron expression into human-readable text
function formatSchedule(workflow) {
  if (!workflow.schedule_enabled || !workflow.schedule_cron) {
    return null;
  }

  const cron = workflow.schedule_cron;
  
  // Common schedule patterns
  const schedulePatterns = {
    '0 * * * *': 'Every hour',
    '0 8 * * *': 'Daily at 8:00 AM',
    '0 9 * * *': 'Daily at 9:00 AM',
    '0 9 * * 1-5': 'Weekdays at 9:00 AM',
    '0 9 * * 1': 'Weekly on Monday',
    '0 9 1 * *': 'Monthly on 1st',
    '0 0 * * *': 'Daily at midnight',
    '*/15 * * * *': 'Every 15 minutes',
    '*/30 * * * *': 'Every 30 minutes',
  };

  return schedulePatterns[cron] || 'Custom schedule';
}
